import {
  Dispatch,
  forwardRef,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";

import { Loader } from "@/components";
import { ExitFullScreen, FullScreen, Play } from "@/icons";
import { useResponsiveness, useTranslate } from "@/providers/context";
import {
  cn,
  getLanguageCode,
  getPreferredSubtitleLanguage,
  getUrlFromFilename,
  isSubtitlesEnabled,
} from "@/utils";
import { useAppProperties } from "@/hooks";

import { useCustomPlayer } from "../../hooks/useCustomPlayer";
import { VideoQualityOptions } from "../HLSVideoPlayer";
import { SettingsDropdown } from "./SettingsDropdown";
import { VolumeSlider } from "./VolumeSlider";
import { TimeDisplay } from "./TimeDisplay";
import { PlayButton } from "./PlayButton";
import { Scrubber } from "./Scrubber";
import { SubtitlesDropdown } from "./SubtitlesDropdown";

interface CustomVideoPlayerProps {
  playing: boolean;
  setPlaying: Dispatch<SetStateAction<boolean>>;
  autoPlay?: boolean;
  thumbnail: string | undefined;
  itemId: number;
  qualities: VideoQualityOptions[];
  currentQuality: number;
  onQualityChange: (level: number) => void;
  subtitleTracks?: HTMLTrackElement[];
  handleReplay: () => void;
  isLive: boolean;
}

export const CustomVideoPlayer = forwardRef<
  HTMLVideoElement,
  CustomVideoPlayerProps
>(
  (
    {
      autoPlay,
      playing,
      setPlaying,
      thumbnail,
      qualities,
      currentQuality,
      onQualityChange,
      subtitleTracks,
      handleReplay,
      isLive,
    },
    ref,
  ) => {
    const playerRef = ref as RefObject<HTMLVideoElement>;
    const {
      togglePlay,
      toggleMute,
      handleVolumeChange,
      handleTimeUpdate,
      handleProgress,
      handleLoadedMetadata,
      handleSeek,
      handleScrub,
      handleSkipBack,
      handleSkipForward,
      volume,
      muted,
      currentTime,
      duration,
      started,
      progressPercentage,
      bufferedPercentage,
    } = useCustomPlayer({
      playerRef,
      playing,
      setPlaying,
      autoPlay,
      isLive,
    });

    const { isFullscreen } = useResponsiveness();
    const { translate } = useTranslate();
    const { data: properties, subtitleDisabled } = useAppProperties();

    const [isLoaded, setIsLoaded] = useState(false);
    const [overlayVisible, setOverlayVisible] = useState(false);
    const [overlayTimeout, setOverlayTimeout] = useState<NodeJS.Timeout | null>(
      null,
    );
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const [activeSubtitleId, setActiveSubtitleId] = useState<string | null>(
      null,
    );
    const [defaultSubtitleId, setDefaultSubtitleId] = useState<string>();
    const thumbnailUrl = thumbnail ? getUrlFromFilename(thumbnail) : undefined;
    const preferredSubtitleLanguage = getPreferredSubtitleLanguage();

    const getFullScreenIcon = () => {
      if (isFullscreen) {
        return <ExitFullScreen className="h-6 w-6" />;
      } else {
        return <FullScreen className="h-6 w-6" />;
      }
    };

    const toggleFullscreen = useCallback(() => {
      if (isFullscreen) {
        document.exitFullscreen();
      } else {
        playerRef.current?.parentElement?.requestFullscreen();
      }
    }, [playerRef, isFullscreen]);

    const handleSpeedChange = useCallback(
      (speed: number) => {
        if (playerRef.current) {
          playerRef.current.playbackRate = speed;
          setPlaybackSpeed(speed);
        }
      },
      [playerRef, setPlaybackSpeed],
    );

    const getLiveTag = () => {
      if (isLive) {
        return (
          <div className="p2-semibold rounded-[4px] bg-surface-live px-[5px] py-0.5">
            <div className="flex items-center gap-[2px]">
              <div className="h-[5px] w-[5px] rounded-full bg-white" />
              {translate("LIVE")}
            </div>
          </div>
        );
      }
      return null;
    };

    const clearExistingTimeout = useCallback(() => {
      if (overlayTimeout) {
        clearTimeout(overlayTimeout);
        setOverlayTimeout(null);
      }
    }, [overlayTimeout]);

    const setHideOverlayTimeout = useCallback(() => {
      const timeoutId = setTimeout(() => {
        setOverlayVisible(false);
      }, 2000);
      setOverlayTimeout(timeoutId);
    }, []);

    const handleMouseMove = useCallback(() => {
      setOverlayVisible(true);
      clearExistingTimeout();
      setHideOverlayTimeout();
    }, [clearExistingTimeout, setHideOverlayTimeout]);

    const handleMouseLeave = useCallback(() => {
      setOverlayVisible(false);
      clearExistingTimeout();
    }, [clearExistingTimeout]);

    useEffect(() => {
      if (playerRef.current && !subtitleDisabled) {
        // Timeout is a hacky solution to a bug where multiple subtitle tracks are sometimes enabled
        setTimeout(() => {
          if (playerRef.current?.textTracks) {
            Array.from(playerRef.current.textTracks).forEach((track) => {
              track.mode =
                track.id === activeSubtitleId ? "showing" : "disabled";
            });
          }
        }, 100);
      }
    }, [activeSubtitleId, playerRef, subtitleDisabled]);

    useEffect(() => {
      if (subtitleDisabled || !subtitleTracks || !properties) {
        return;
      }

      const appLanguageCode = getLanguageCode(properties?.Language);
      const defaultLanguageCode = preferredSubtitleLanguage ?? appLanguageCode;

      if (defaultLanguageCode) {
        const track = subtitleTracks.find(
          (track) => track.srclang === defaultLanguageCode,
        );

        if (track) {
          setDefaultSubtitleId(track.id);
          if (isSubtitlesEnabled()) {
            setActiveSubtitleId(track.id);
          }
        }
      }
    }, [
      subtitleDisabled,
      subtitleTracks,
      preferredSubtitleLanguage,
      properties,
    ]);

    return (
      <div className="relative flex aspect-video items-center rounded-2xl bg-black text-white">
        {(!started || !isLoaded) && (
          <div
            className="absolute bottom-0 left-0 right-0 top-0 z-40 flex cursor-pointer items-center justify-center bg-cover bg-center hover:text-white/85"
            style={{
              backgroundImage:
                !isLive && thumbnailUrl ? `url('${thumbnailUrl}')` : "",
            }}
            onClick={() => {
              togglePlay();
              handleMouseMove();
            }}
          >
            {started && !isLoaded ? (
              <Loader className="h-[72px] w-[72px]" />
            ) : (
              <Play className="h-[62px] w-[72px]" />
            )}
          </div>
        )}
        <video
          ref={ref}
          className="h-full w-full [&::cue]:leading-normal"
          onTimeUpdate={handleTimeUpdate}
          onProgress={handleProgress}
          onLoadedMetadata={handleLoadedMetadata}
          onLoadedData={() => setIsLoaded(true)}
          onEnded={() => setPlaying(false)}
          autoPlay={autoPlay}
          onCanPlay={() => {
            if (autoPlay) {
              togglePlay(true);
            }
          }}
        >
          {!subtitleDisabled &&
            subtitleTracks?.map(({ id, src, label, srclang }) => (
              <track
                key={id}
                id={id}
                src={src}
                label={label}
                srcLang={srclang}
                kind="subtitles"
              />
            ))}
          {translate("Your browser does not support this video format.")}
        </video>
        <div
          className={cn(
            "absolute bottom-0 left-0 right-0 top-0 flex flex-col justify-between transition-opacity duration-300",
            started && (overlayVisible || !playing)
              ? "opacity-100"
              : "opacity-0",
          )}
          onClick={() => {
            !isLive && togglePlay();
          }}
          onDoubleClick={toggleFullscreen}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          {/* Top Gradient */}
          <div className="absolute left-0 right-0 top-0 h-[15%] bg-gradient-to-t from-transparent from-[12.86%] to-[rgba(0,0,0,0.44)] to-[67.14%]" />
          {/* Bottom Gradient */}
          <div className="absolute bottom-0 left-0 right-0 h-[30%] bg-gradient-to-b from-transparent from-[12.86%] to-[rgba(0,0,0,0.75)] to-[67.14%]" />
          {/* Top Controls */}
          <div className="z-10 flex h-[15%] w-full items-center justify-end px-7">
            {getLiveTag()}
          </div>
          {/* Bottom Controls */}
          <div className="z-30 w-full p-7" onClick={(e) => e.stopPropagation()}>
            {!isLive && (
              <Scrubber
                handleSeek={handleSeek}
                handleScrub={handleScrub}
                progressPercentage={progressPercentage}
                bufferedPercentage={bufferedPercentage}
              />
            )}
            <div className="mt-2 flex items-center justify-between px-4">
              <div className="flex items-center gap-5">
                {!isLive && (
                  <PlayButton
                    playing={playing}
                    togglePlay={togglePlay}
                    progressPercentage={progressPercentage}
                    handleReplay={handleReplay}
                  />
                )}
                {!isLive && (
                  <TimeDisplay
                    duration={duration}
                    currentTime={currentTime}
                    handleSkipBack={handleSkipBack}
                    handleSkipForward={handleSkipForward}
                  />
                )}
                <VolumeSlider
                  volume={volume}
                  handleVolumeChange={handleVolumeChange}
                  muted={muted}
                  toggleMute={toggleMute}
                />
              </div>
              <div className="flex items-center gap-5">
                <button onClick={toggleFullscreen} className="hover:opacity-85">
                  {getFullScreenIcon()}
                </button>
                {!subtitleDisabled && !isLive && (
                  <SubtitlesDropdown
                    subtitleTracks={subtitleTracks}
                    activeSubtitleId={activeSubtitleId}
                    setActiveSubtitleId={setActiveSubtitleId}
                    defaultSubtitleId={defaultSubtitleId}
                  />
                )}
                {!isLive && (
                  <SettingsDropdown
                    qualities={qualities}
                    currentQuality={currentQuality}
                    currentSpeed={playbackSpeed}
                    onQualityChange={onQualityChange}
                    onSpeedChange={handleSpeedChange}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        <div id="fullscreen-player-portal" className="z-20" />
      </div>
    );
  },
);

CustomVideoPlayer.displayName = "CustomVideoPlayer";
